import * as msTeams from '@microsoft/teams-js';
import { isTokenUsable } from './jwt-helper';
import * as storageHelper from './storage-helper';

export type MsSsoTokenProvider = Pick<typeof msTeams, 'authentication'>;

// Some Teams hosts never answer a token request; the caller must not hang on it (TEAM-1883).
export const MS_SSO_TOKEN_TIMEOUT_MS = 10000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Teams SSO token request timed out after ${timeoutMs} ms`)), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

// Teams SSO tokens live about an hour. The cookie copy is reused while it is valid; otherwise a fresh token is
// requested from the Teams client, so the backend never has to accept an expired one (TEAM-1883). Returns undefined
// when no usable token can be obtained; callers then take the login path instead of sending a token that is
// guaranteed to be rejected.
export async function ensureMsSsoToken(
  engine: MsSsoTokenProvider = msTeams,
  timeoutMs: number = MS_SSO_TOKEN_TIMEOUT_MS
): Promise<string | undefined> {
  const storedToken = storageHelper.getMsSsoToken();
  if (isTokenUsable(storedToken)) {
    return storedToken;
  }

  try {
    // Wrapped in a promise so a synchronous throw from teams-js (library not initialized) ends up in the catch.
    const request = Promise.resolve().then(() => engine.authentication.getAuthToken());
    const freshToken = await withTimeout(request, timeoutMs);
    if (freshToken) {
      storageHelper.setMsSsoToken(freshToken);
      return freshToken;
    }
  } catch (error) {
    console.warn('ensureMsSsoToken: could not get a new Teams SSO token', error);
  }

  return undefined;
}
